import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Card,
    Description,
    FieldError,
    Form,
    Label,
    NumberField,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { StepBackButton } from '@/pages/catalogImport/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ACCOUNTING_YEAR_MAX,
    ACCOUNTING_YEAR_MIN,
    type UseAccountingStartStepResult,
} from './useAccountingStartStep';

type AccountingStartStepViewProps = Omit<
    UseAccountingStartStepResult,
    'isNotNeeded'
>;

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/**
 * Accounting start: the title, why it is needed, one year field, and the
 * card's footer: **← Back** (to Welcome, `StepBackButton` with the `start`
 * target, which reads "Back") leftmost and Continue rightmost; below `sm`
 * Continue first and Back last, both full width (layout classes reorder
 * them, Back stays first in the DOM). Back waits while the start runs. A
 * closed year is the field's own error; any other refusal is a notice above
 * the footer with Retry.
 */
export const AccountingStartStepView: React.FC<
    AccountingStartStepViewProps
> = ({
    year,
    onYearChange,
    yearError,
    closedYear,
    failure,
    isSubmitting,
    onContinue,
    onBack,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            className="flex flex-col gap-6 py-4 sm:py-8"
            {...getNodeTestIdProp('onboarding-accounting')}
        >
            <div className="flex flex-col gap-2">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('onboarding-accounting-title')}
                >
                    {t('onboarding.accounting.title')}
                </Typography>
                <Typography.Paragraph color="muted">
                    {t('onboarding.accounting.intro')}
                </Typography.Paragraph>
            </div>
            <Card>
                <Form
                    className="flex flex-col gap-6"
                    onSubmit={(event) => {
                        event.preventDefault();
                        onContinue();
                    }}
                    validationBehavior="aria"
                >
                    <Card.Content>
                        {/* Read-only, not disabled, while the request runs:
                            disabling the focused field (Enter submits from
                            it) blurred it in Chrome, and its blur committed
                            the year it showed before over the one sent. */}
                        <NumberField
                            formatOptions={{ useGrouping: false }}
                            fullWidth
                            isInvalid={yearError !== null}
                            isReadOnly={isSubmitting}
                            maxValue={ACCOUNTING_YEAR_MAX}
                            minValue={ACCOUNTING_YEAR_MIN}
                            onChange={(next) =>
                                onYearChange(
                                    next === undefined || Number.isNaN(next)
                                        ? null
                                        : next,
                                )
                            }
                            value={year ?? Number.NaN}
                            variant="secondary"
                            {...getNodeTestIdProp('onboarding-accounting-year')}
                        >
                            <Label>
                                {t('onboarding.accounting.yearLabel')}
                            </Label>
                            <NumberField.Group className="w-full">
                                <NumberField.DecrementButton />
                                <NumberField.Input className="tabular-nums" />
                                <NumberField.IncrementButton />
                            </NumberField.Group>
                            {yearError === 'closed' ? (
                                <FieldError
                                    {...getNodeTestIdProp(
                                        'onboarding-accounting-year-error',
                                    )}
                                >
                                    {t('onboarding.accounting.yearClosed', {
                                        year: closedYear ?? year ?? '',
                                    })}
                                </FieldError>
                            ) : yearError === 'range' ? (
                                <FieldError
                                    {...getNodeTestIdProp(
                                        'onboarding-accounting-year-error',
                                    )}
                                >
                                    {t('onboarding.accounting.yearRange', {
                                        min: ACCOUNTING_YEAR_MIN,
                                        max: ACCOUNTING_YEAR_MAX,
                                    })}
                                </FieldError>
                            ) : (
                                <Description>
                                    {t('onboarding.accounting.yearHelp')}
                                </Description>
                            )}
                        </NumberField>
                    </Card.Content>
                    {failure ? (
                        <Card.Content>
                            <Alert
                                status="danger"
                                {...getNodeTestIdProp(
                                    'onboarding-accounting-error',
                                )}
                            >
                                <Alert.Indicator />
                                <Alert.Content>
                                    <Alert.Title>
                                        {t('onboarding.accounting.errorTitle')}
                                    </Alert.Title>
                                    <Alert.Description>
                                        {failure.message ||
                                            t(
                                                'onboarding.accounting.errorFallback',
                                            )}
                                    </Alert.Description>
                                    <div className="pt-2">
                                        <Button
                                            isDisabled={isSubmitting}
                                            onPress={onContinue}
                                            size="sm"
                                            variant="secondary"
                                            {...getNodeTestIdProp(
                                                'onboarding-accounting-retry',
                                            )}
                                        >
                                            {t('onboarding.accounting.retry')}
                                        </Button>
                                    </div>
                                </Alert.Content>
                            </Alert>
                        </Card.Content>
                    ) : null}
                    <Card.Footer className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <StepBackButton
                            className="order-2 w-full sm:order-1 sm:w-auto"
                            isDisabled={isSubmitting}
                            onPress={onBack}
                            target="start"
                        />
                        <Button
                            className="order-1 w-full sm:order-2 sm:w-auto"
                            isPending={isSubmitting}
                            type="submit"
                            {...getNodeTestIdProp(
                                'onboarding-accounting-continue',
                            )}
                        >
                            {t('onboarding.accounting.continue')}
                        </Button>
                    </Card.Footer>
                </Form>
            </Card>
        </section>
    );
};
